import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Google AdSense Ad Component for BoPeDo Debate Engine
 * 
 * SETUP INSTRUCTIES VOOR RONALD:
 * 1. Ga naar https://adsense.google.com/start/ en maak een account
 * 2. Gebruik je website URL en ronaldlubach@gmail.com
 * 3. Na goedkeuring krijg je een Publisher ID (ca-pub-XXXXXXXXXXXXXXXX)
 * 4. Vervang "ca-pub-XXXXXXXXXXXXXXXX" in /public/index.html
 * 5. Maak ad units aan in AdSense dashboard (Display, In-Article, Multiplex)
 * 6. Vul de ad-slot IDs hieronder in
 * 
 * CONTENT FILTERS (in AdSense dashboard instellen):
 * - Blokkeer: Adult, Gokken, Alcohol, Drugs/Medicijnen, Wapens
 * - Sta toe: Technologie, Onderwijs, Business, Wetenschap, Entertainment
 */

// Ad slot IDs — vul deze in na het aanmaken van ad units in AdSense
const AD_SLOTS = {
  banner_top: "",       // Display ad (728x90 of responsive)
  inline_article: "",   // In-article ad
  banner_bottom: "",    // Display ad (728x90 of responsive)
  multiplex: "",        // Multiplex ad (meerdere ads in grid)
};

// Check of AdSense geladen is
const isAdSenseLoaded = () => {
  return typeof window !== "undefined" && window.adsbygoogle !== undefined;
};

const AdSenseUnit = ({ slot, format = "auto", responsive = true, className = "" }) => {
  const adRef = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (!slot || pushed.current) return;
    
    try {
      if (isAdSenseLoaded() && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      }
    } catch (err) {
      // AdSense kan geblokkeerd zijn door adblocker — non-fataal, fallback CTA wordt getoond
      if (process.env.NODE_ENV !== "production") console.debug("[AdSense] push failed:", err?.message);
    }
  }, [slot]);

  if (!slot) return null;

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ${className}`}
      style={{ display: "block" }}
      data-ad-client="ca-pub-0931745449634758"
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive={responsive ? "true" : "false"}
    />
  );
};

// Fallback premium CTA wanneer AdSense nog niet actief is
const FallbackAd = ({ variant, navigate }) => {
  if (variant === "hero") return (
    <div className="bg-gradient-to-r from-[#0a0e1a] to-[#1a2744] text-white p-4">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#f59e0b]">Advertentie</p>
          <p className="text-sm mt-1">Ontdek wat 6+ AI-experts tegelijk voor jou kunnen doen.</p>
        </div>
        <button onClick={() => navigate("/premium")} className="px-4 py-2 bg-[#f59e0b] text-[#0a0e1a] text-xs font-bold hover:bg-[#d97706] transition-colors flex-shrink-0">
          BEKIJK PREMIUM
        </button>
      </div>
    </div>
  );

  if (variant === "between") return (
    <div className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#A1A1AA] border border-[#E5E5E5] px-1.5 py-0.5">AD</span>
        <p className="text-xs text-[#52525B]">Geen advertenties + meer debaters?</p>
      </div>
      <button onClick={() => navigate("/premium")} className="text-xs font-bold text-[#2d8cf0] hover:underline">Upgrade</button>
    </div>
  );

  return (
    <div className="bg-[#FAFAFA] border border-dashed border-[#E5E5E5] p-4 text-center text-xs text-[#52525B]">
      ADVERTENTIE — <button onClick={() => navigate("/premium")} className="underline hover:text-[#2d8cf0] font-medium">Upgrade naar Premium</button> voor ad-vrij + meer debaters
    </div>
  );
};

// Resolve which AdSense slot to use based on explicit slot key or variant fallback
const resolveAdSlot = (slot, variant) => {
  if (slot) return AD_SLOTS[slot];
  if (variant === "hero") return AD_SLOTS.banner_bottom;
  if (variant === "between") return AD_SLOTS.inline_article;
  return AD_SLOTS.banner_top;
};

// Best-effort impression tracking — intentionally swallows network errors
const trackImpression = (variant, hasAdsense, page) => {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const body = page ? { type: variant, page, has_adsense: hasAdsense } : { type: variant, has_adsense: hasAdsense };
  fetch(`${API}/ads/impression`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch((err) => {
    if (process.env.NODE_ENV !== "production") console.debug("[Ad impression] tracking failed:", err?.message);
  });
};

/**
 * Main Ad Component — shows AdSense if configured, otherwise premium CTA fallback
 * 
 * @param {string} variant - "between" | "inline" | "hero"
 * @param {string} slot - AdSense ad slot key from AD_SLOTS
 * @param {string} className - extra CSS classes
 */
export const AdBanner = ({ variant = "inline", slot, className = "" }) => {
  const navigate = useNavigate();
  const adSlotId = resolveAdSlot(slot, variant);

  useEffect(() => {
    trackImpression(variant, !!adSlotId);
  }, [variant, adSlotId]);

  return (
    <div className={className} data-testid={`ad-banner-${variant}`}>
      {adSlotId ? (
        <AdSenseUnit
          slot={adSlotId}
          format={variant === "between" ? "fluid" : "auto"}
          responsive={true}
        />
      ) : (
        <FallbackAd variant={variant} navigate={navigate} />
      )}
    </div>
  );
};

/**
 * Discussion-specific ad component
 */
export const DiscussionAd = ({ variant = "between", className = "" }) => {
  const navigate = useNavigate();
  const adSlotId = variant === "hero" ? AD_SLOTS.banner_bottom : AD_SLOTS.inline_article;

  useEffect(() => {
    trackImpression(variant, !!adSlotId, "discussion");
  }, [variant, adSlotId]);

  if (adSlotId) {
    return (
      <div className={className} data-testid={`ad-discussion-${variant}`}>
        <AdSenseUnit slot={adSlotId} format="fluid" responsive={true} />
      </div>
    );
  }

  // Fallback
  if (variant === "between") return (
    <div className={`bg-white border border-gray-200 p-3 flex items-center justify-between my-4 ${className}`} data-testid={`ad-discussion-${variant}`}>
      <div className="flex items-center gap-3">
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 border border-gray-200 px-1.5 py-0.5">AD</span>
        <p className="text-xs text-gray-500">Wil je 6+ experts tegelijk? Geen advertenties?</p>
      </div>
      <button onClick={() => navigate("/premium")} className="text-xs font-bold text-[#2d8cf0] hover:underline flex-shrink-0">Bekijk Premium</button>
    </div>
  );

  return (
    <div className={`bg-gradient-to-r from-[#0a0e1a] to-[#1a2744] text-white p-4 my-4 ${className}`} data-testid={`ad-discussion-${variant}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#f59e0b]">Advertentie</p>
          <p className="text-sm mt-1">Diepere analyse nodig? Premium geeft je meer experts en betere modellen.</p>
        </div>
        <button onClick={() => navigate("/premium")} className="px-4 py-2 bg-[#f59e0b] text-[#0a0e1a] text-xs font-bold hover:bg-[#d97706] transition-colors flex-shrink-0">
          PREMIUM
        </button>
      </div>
    </div>
  );
};
